import { Skeleton, Stack, Typography } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import ToolIcon from '@/components/Icons/ToolIcon';
import { iconForStatus } from '@/components/StatusBadge/StatusBadge';
import { DeploymentStatus } from '@/graphql';
import { BasetenPageEnum } from '@/routes/types';

import { usePinnedAPI as usePinnedChainsAPI } from '../../Chains/hooks';
import { usePinnedAPI as usePinnedEndpointsAPI } from '../../ModelAPIs/hooks';
import { usePinnedAPI as usePinnedModelsAPI } from '../../Models/hooks';
import { usePinnedAPI as usePinnedTrainingProjectsAPI } from '../../Training/hooks';
import { AppLink, MenuItem } from '../styled';
import { Container, EntityName } from './styled';

function Pinned() {
  const {
    pinnedChains,
    loading: pinnedChainsLoading,
    getPinActionItem: getPinChainActionItem,
  } = usePinnedChainsAPI();
  const {
    pinnedEndpoints,
    loading: pinnedEndpointsLoading,
    getPinActionItem: getPinEndpointActionItem,
  } = usePinnedEndpointsAPI();
  const {
    pinnedModels,
    loading: pinnedModelsLoading,
    getPinActionItem: getPinModelActionItem,
  } = usePinnedModelsAPI();
  const {
    pinnedTrainingProjects,
    loading: pinnedTrainingProjectsLoading,
    getPinActionItem: getPinTrainingProjectActionItem,
  } = usePinnedTrainingProjectsAPI();

  function getContent() {
    if (
      pinnedChainsLoading ||
      pinnedEndpointsLoading ||
      pinnedModelsLoading ||
      pinnedTrainingProjectsLoading
    ) {
      return (
        <Stack pt={1}>
          <Skeleton variant="text" width={110} />
          <Skeleton variant="text" width={230} />
          <Skeleton variant="text" width={190} />
          <Skeleton variant="text" width={150} />
          <Skeleton variant="text" width={210} />
        </Stack>
      );
    }

    if (
      isEmpty(pinnedChains) &&
      isEmpty(pinnedEndpoints) &&
      isEmpty(pinnedModels) &&
      isEmpty(pinnedTrainingProjects)
    ) {
      return (
        <Stack alignItems="center" justifyContent="center" height={150}>
          <Typography color="text.muted" variant="body2">
            Nothing pinned yet
          </Typography>
        </Stack>
      );
    }

    function renderDeployments() {
      return pinnedModels.map((model) => {
        return (
          <MenuItem key={model.id}>
            <AppLink page={BasetenPageEnum.Model} params={{ modelId: model.id }}>
              {iconForStatus[model.primaryVersion.currentModelDeploymentStatus.status]}
              <EntityName>{model.name}</EntityName>
            </AppLink>
            <Stack data-cy="actions-menu">{getPinModelActionItem(model.id, model.name)}</Stack>
          </MenuItem>
        );
      });
    }

    function renderChains() {
      return pinnedChains.map((chain) => (
        <MenuItem key={chain.id}>
          <AppLink page={BasetenPageEnum.Chain} params={{ chainId: chain.id }}>
            {iconForStatus[chain.primaryDeployment.status]} <EntityName>{chain.name}</EntityName>
          </AppLink>
          <Stack data-cy="actions-menu">{getPinChainActionItem(chain.id, chain.name)}</Stack>
        </MenuItem>
      ));
    }

    function renderModelAPIs() {
      return pinnedEndpoints.map((endpoint) => (
        <MenuItem key={endpoint.id}>
          <AppLink page={BasetenPageEnum.SharedEndpoint} params={{ sharedEndpointId: endpoint.id }}>
            {iconForStatus[DeploymentStatus.ModelReady]}{' '}
            <EntityName>{endpoint.sharedEndpoint.name}</EntityName>
          </AppLink>
          <Stack data-cy="actions-menu">
            {getPinEndpointActionItem(endpoint.id, endpoint.sharedEndpoint.name)}
          </Stack>
        </MenuItem>
      ));
    }

    function renderTrainingJobs() {
      return pinnedTrainingProjects.map((trainingProject) => (
        <MenuItem key={trainingProject.id}>
          <AppLink
            page={BasetenPageEnum.TrainingProject}
            params={{ trainingProjectId: trainingProject.id }}
          >
            <ToolIcon color="secondary" /> <EntityName>{trainingProject.name}</EntityName>
          </AppLink>
          <Stack data-cy="actions-menu">
            {getPinTrainingProjectActionItem(trainingProject.id, trainingProject.name)}
          </Stack>
        </MenuItem>
      ));
    }

    return (
      <Stack gap={0.5}>
        {renderDeployments()}
        {renderChains()}
        {renderModelAPIs()}
        {renderTrainingJobs()}
      </Stack>
    );
  }

  return (
    <Container>
      <Typography color="text.muted" variant="h6" sx={{ userSelect: 'none', pb: 1 }}>
        Pinned
      </Typography>
      {getContent()}
    </Container>
  );
}

export default Pinned;
